import { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { scaleQuantize } from "d3-scale";
import { GlobeHemisphereWest, MapPin, Users } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { countryName } from "@/lib/locations";

// Free, no-token world topology (TopoJSON, ~99 KB).
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// world-atlas uses numeric ISO 3166-1 codes (`id` is a 3-digit string).
// We need to convert them to alpha-2 codes that we store in `user.country`.
// Mapping covers the most common cases; unknown countries display in dim grey.
const NUMERIC_TO_ALPHA2 = {
  "004":"AF","008":"AL","010":"AQ","012":"DZ","016":"AS","020":"AD","024":"AO","028":"AG","031":"AZ","032":"AR","036":"AU","040":"AT","044":"BS","048":"BH","050":"BD","051":"AM","052":"BB","056":"BE","060":"BM","064":"BT","068":"BO","070":"BA","072":"BW","074":"BV","076":"BR","084":"BZ","086":"IO","090":"SB","092":"VG","096":"BN","100":"BG","104":"MM","108":"BI","112":"BY","116":"KH","120":"CM","124":"CA","132":"CV","136":"KY","140":"CF","144":"LK","148":"TD","152":"CL","156":"CN","158":"TW","162":"CX","166":"CC","170":"CO","174":"KM","175":"YT","178":"CG","180":"CD","184":"CK","188":"CR","191":"HR","192":"CU","196":"CY","203":"CZ","204":"BJ","208":"DK","212":"DM","214":"DO","218":"EC","222":"SV","226":"GQ","231":"ET","232":"ER","233":"EE","234":"FO","238":"FK","239":"GS","242":"FJ","246":"FI","248":"AX","250":"FR","254":"GF","258":"PF","260":"TF","262":"DJ","266":"GA","268":"GE","270":"GM","275":"PS","276":"DE","288":"GH","292":"GI","296":"KI","300":"GR","304":"GL","308":"GD","312":"GP","316":"GU","320":"GT","324":"GN","328":"GY","332":"HT","334":"HM","336":"VA","340":"HN","344":"HK","348":"HU","352":"IS","356":"IN","360":"ID","364":"IR","368":"IQ","372":"IE","376":"IL","380":"IT","384":"CI","388":"JM","392":"JP","398":"KZ","400":"JO","404":"KE","408":"KP","410":"KR","414":"KW","417":"KG","418":"LA","422":"LB","426":"LS","428":"LV","430":"LR","434":"LY","438":"LI","440":"LT","442":"LU","446":"MO","450":"MG","454":"MW","458":"MY","462":"MV","466":"ML","470":"MT","474":"MQ","478":"MR","480":"MU","484":"MX","492":"MC","496":"MN","498":"MD","499":"ME","500":"MS","504":"MA","508":"MZ","512":"OM","516":"NA","520":"NR","524":"NP","528":"NL","531":"CW","533":"AW","534":"SX","535":"BQ","540":"NC","548":"VU","554":"NZ","558":"NI","562":"NE","566":"NG","570":"NU","574":"NF","578":"NO","580":"MP","581":"UM","583":"FM","584":"MH","585":"PW","586":"PK","591":"PA","598":"PG","600":"PY","604":"PE","608":"PH","612":"PN","616":"PL","620":"PT","624":"GW","626":"TL","630":"PR","634":"QA","638":"RE","642":"RO","643":"RU","646":"RW","652":"BL","654":"SH","659":"KN","660":"AI","662":"LC","663":"MF","666":"PM","670":"VC","674":"SM","678":"ST","682":"SA","686":"SN","688":"RS","690":"SC","694":"SL","702":"SG","703":"SK","704":"VN","705":"SI","706":"SO","710":"ZA","716":"ZW","724":"ES","728":"SS","729":"SD","732":"EH","740":"SR","744":"SJ","748":"SZ","752":"SE","756":"CH","760":"SY","762":"TJ","764":"TH","768":"TG","772":"TK","776":"TO","780":"TT","784":"AE","788":"TN","792":"TR","795":"TM","796":"TC","798":"TV","800":"UG","804":"UA","807":"MK","818":"EG","826":"GB","831":"GG","832":"JE","833":"IM","834":"TZ","840":"US","850":"VI","854":"BF","858":"UY","860":"UZ","862":"VE","876":"WF","882":"WS","887":"YE","894":"ZM",
};

export default function WorldHeatmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/players-by-country");
        setData(res.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const { byCode, maxCount } = useMemo(() => {
    const m = {};
    let max = 0;
    (data?.countries || []).forEach((c) => {
      if (c.code === "UNKNOWN") return;
      m[c.code] = c.count;
      if (c.count > max) max = c.count;
    });
    return { byCode: m, maxCount: max };
  }, [data]);

  // 6-step lime ramp on a dark base. d3.scaleQuantize maps [0, max] → palette.
  const colorScale = useMemo(() => {
    return scaleQuantize().domain([1, Math.max(maxCount, 1)]).range([
      "#1E2A0B", "#3B5618", "#587B22", "#79A12B", "#A6CE2E", "#CCFF00",
    ]);
  }, [maxCount]);

  const total = data?.total || 0;
  const unknown = (data?.countries || []).find((c) => c.code === "UNKNOWN")?.count || 0;
  const countryCount = (data?.countries || []).filter((c) => c.code !== "UNKNOWN").length;

  return (
    <div className="space-y-6" data-testid="world-heatmap-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2">
          <GlobeHemisphereWest size={14} weight="fill" /> Mapa-Calor Global
        </p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">
          Jugadores Registrados
        </h1>
        <p className="text-sm text-gray-400 mt-1">Distribución geográfica de la comunidad DyD.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-black/40 border-white/10"><CardContent className="p-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400">Total jugadores</p>
          <p className="text-3xl font-black text-[#CCFF00] mt-1" data-testid="hm-total">{total}</p>
        </CardContent></Card>
        <Card className="bg-black/40 border-white/10"><CardContent className="p-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400">Países activos</p>
          <p className="text-3xl font-black text-white mt-1" data-testid="hm-countries">{countryCount}</p>
        </CardContent></Card>
        <Card className="bg-black/40 border-white/10"><CardContent className="p-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400">Sin ubicación</p>
          <p className="text-3xl font-black text-gray-300 mt-1" data-testid="hm-unknown">{unknown}</p>
        </CardContent></Card>
        <Card className="bg-black/40 border-white/10"><CardContent className="p-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400">País líder</p>
          <p className="text-base font-bold text-white mt-1 truncate" data-testid="hm-leader">
            {(() => {
              const lead = (data?.countries || []).find((c) => c.code !== "UNKNOWN");
              return lead ? `${countryName(lead.code)} (${lead.count})` : "—";
            })()}
          </p>
        </CardContent></Card>
      </div>

      <Card className="bg-black/40 border-white/10 overflow-hidden">
        <CardHeader>
          <CardTitle className="text-sm tracking-widest uppercase text-gray-200">Mapa Coropleta</CardTitle>
        </CardHeader>
        <CardContent className="p-2 sm:p-4 relative">
          {loading && <p className="text-center text-gray-500 py-12">Cargando datos...</p>}
          {!loading && (
            <div className="relative" data-testid="world-map-svg" style={{ touchAction: "pan-y" }}>
              <ComposableMap projectionConfig={{ scale: 150 }} width={980} height={500} style={{ width: "100%", height: "auto" }}>
                <ZoomableGroup zoom={1}>
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) => geographies.map((geo) => {
                      const alpha2 = NUMERIC_TO_ALPHA2[geo.id];
                      const count = alpha2 ? (byCode[alpha2] || 0) : 0;
                      const fill = count > 0 ? colorScale(count) : "#1F1F1F";
                      const name = countryName(alpha2) || geo.properties?.name || "—";
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fill}
                          stroke="#0A0A0A"
                          strokeWidth={0.4}
                          onMouseEnter={(e) => {
                            setHovered({ name, count, code: alpha2 || geo.id });
                            setTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setHovered(null)}
                          style={{
                            default: { outline: "none", cursor: count > 0 ? "pointer" : "default" },
                            hover: { fill: count > 0 ? "#FFFFFF" : "#2A2A2A", outline: "none" },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    })}
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>
              {hovered && (
                <div
                  className="fixed z-50 pointer-events-none bg-black border border-[#CCFF00]/40 rounded px-3 py-2 text-xs shadow-xl"
                  style={{ left: tooltipPos.x + 12, top: tooltipPos.y + 12 }}
                  data-testid="world-map-tooltip"
                >
                  <p className="text-[#CCFF00] font-bold">{hovered.name}</p>
                  <p className="text-white">{hovered.count} jugador{hovered.count === 1 ? "" : "es"}</p>
                </div>
              )}
            </div>
          )}
          {/* Legend */}
          {!loading && maxCount > 0 && (
            <div className="mt-4 flex items-center gap-2 text-[10px] text-gray-400 uppercase tracking-wider">
              <span>0</span>
              <div className="flex h-2 flex-1 rounded overflow-hidden">
                {["#1E2A0B","#3B5618","#587B22","#79A12B","#A6CE2E","#CCFF00"].map((c) => (
                  <div key={c} className="flex-1" style={{ background: c }} />
                ))}
              </div>
              <span>{maxCount}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-black/40 border-white/10">
          <CardHeader><CardTitle className="text-sm tracking-widest uppercase text-gray-200 flex items-center gap-2"><Users size={14} className="text-[#CCFF00]" /> Top países</CardTitle></CardHeader>
          <CardContent className="p-4">
            <ul className="space-y-2" data-testid="hm-top-countries">
              {(data?.countries || []).filter((c) => c.code !== "UNKNOWN").slice(0, 10).map((c, i) => (
                <li key={c.code} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5 last:border-0">
                  <span className="flex items-center gap-2 text-gray-200">
                    <span className="text-[10px] text-gray-500 w-5">{i + 1}.</span>
                    <span>{countryName(c.code)}</span>
                  </span>
                  <span className="font-bold text-[#CCFF00]">{c.count}</span>
                </li>
              ))}
              {(data?.countries || []).filter((c) => c.code !== "UNKNOWN").length === 0 && (
                <li className="text-xs text-gray-500">Aún no hay jugadores con país registrado.</li>
              )}
            </ul>
          </CardContent>
        </Card>
        <Card className="bg-black/40 border-white/10">
          <CardHeader><CardTitle className="text-sm tracking-widest uppercase text-gray-200 flex items-center gap-2"><MapPin size={14} className="text-[#CCFF00]" /> Top estados</CardTitle></CardHeader>
          <CardContent className="p-4">
            <ul className="space-y-2" data-testid="hm-top-states">
              {(data?.top_states_global || []).map((s, i) => (
                <li key={`${s.country}-${s.state}-${i}`} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5 last:border-0">
                  <span className="flex items-center gap-2 text-gray-200">
                    <span className="text-[10px] text-gray-500 w-5">{i + 1}.</span>
                    <span>{s.state} <span className="text-gray-500">· {countryName(s.country)}</span></span>
                  </span>
                  <span className="font-bold text-[#CCFF00]">{s.count}</span>
                </li>
              ))}
              {(data?.top_states_global || []).length === 0 && (
                <li className="text-xs text-gray-500">Aún no hay jugadores con estado registrado.</li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
